import type {
    CSSProperties,
    FC,
    PointerEvent as ReactPointerEvent,
    ReactNode
} from 'react'

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

import { FriezeDefs } from '@/components/landing/frieze-defs'
import { supportsGlassBackdrop } from '@/lib/frieze/lens'

import {
    createBeat,
    settleBeat,
    startBeat,
    stepBeat
} from '@/lib/frieze/beat'

import {
    clamp,
    easeInOut,
    easeOutCubic,
    easeOutQuad
} from '@/lib/frieze/math'

import { EASE } from '@/constants/type'
import { FZ } from '@/constants/frieze'
import { PROJECTS } from '@/constants/projects'

const N = PROJECTS.length

type PanelCache = {
    transform: string
    opacity: string
    zIndex: string
    veil: string
    level: number
}

/**
 * Frieze — a full-viewport 3D cover-flow wall.
 *
 * One requestAnimationFrame loop owns every per-frame transform, opacity, filter
 * and the scroll physics. GSAP only tweens the two scalars (`chromeVis`,
 * `openProg`) that the loop then reads, so the two never fight over a transform.
 */
export const Frieze: FC = (): ReactNode => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [openIndex, setOpenIndex] = useState(-1)
    const [glass, setGlass] = useState(false)

    const sectionRef = useRef<HTMLElement | null>(null)
    const cardRefs = useRef<(HTMLButtonElement | null)[]>([])
    const imgRefs = useRef<(HTMLImageElement | null)[]>([])
    const veilRefs = useRef<(HTMLSpanElement | null)[]>([])
    const detailRef = useRef<HTMLButtonElement | null>(null)
    const chromeRef = useRef<HTMLDivElement | null>(null)
    const titleRef = useRef<HTMLHeadingElement | null>(null)
    const descRef = useRef<HTMLParagraphElement | null>(null)
    const counterRef = useRef<HTMLDivElement | null>(null)
    const hintRef = useRef<HTMLDivElement | null>(null)
    const trackRef = useRef<HTMLDivElement | null>(null)
    const fillRef = useRef<HTMLDivElement | null>(null)
    const lensRef = useRef<HTMLDivElement | null>(null)
    const closeRef = useRef<HTMLButtonElement | null>(null)

    /** The two discrete beats. The loop steps them, then reads them, every frame. */
    const beats = useRef({ chromeVis: createBeat(0), openProg: createBeat(0) })

    const engine = useRef({
        w: 0,
        h: 0,
        cardW: 0,
        cardH: 0,
        bandY: 0,
        viewCenter: 0,
        stride: FZ.STRIDE_FRAC as number,
        mobile: false,
        rectLeft: 0,
        rectTop: 0,

        x: 0,
        v: 0,
        prevX: 0,
        lastInput: 0,
        blurV: 0,
        prevOpen: 0,
        arrowTarget: null as number | null,

        drag: {
            active: false,
            id: -1,
            startX: 0,
            startY: 0,
            lastX: 0,
            scrollStart: 0,
            axis: '' as '' | 'h' | 'v',
            moved: false
        },

        open: false,
        openIdx: -1,
        openScrollRef: 0,
        openRel: 0,

        elapsed: 0,
        reduced: false,
        inView: true,
        active: -1,

        lens: {
            px: 0,
            py: 0,
            vx: 0,
            vy: 0,
            tx: 0,
            ty: 0,
            vis: 0,
            hover: false
        },

        cache: PROJECTS.map<PanelCache>(() => ({
            transform: '',
            opacity: '',
            zIndex: '',
            veil: '',
            level: -1
        }))
    })

    const openPanel = useCallback((index: number) => {
        const e = engine.current

        if (e.open || e.drag.moved) return

        e.openScrollRef = Math.round(e.x)

        let rel = ((index - e.openScrollRef) % N + N) % N
        if (rel > N / 2) rel -= N

        e.openRel = rel
        e.open = true
        e.openIdx = index
        e.arrowTarget = null
        e.v = 0

        setOpenIndex(index)

        if (e.reduced) {
            settleBeat(beats.current.openProg, 1)
            return
        }

        startBeat(beats.current.openProg, 1, FZ.OPEN_DUR, easeInOut)
    }, [])

    const closePanel = useCallback(() => {
        const e = engine.current

        if (!e.open) return

        e.open = false

        const finish = () => {
            engine.current.openIdx = -1
            setOpenIndex(-1)
        }

        if (e.reduced) {
            settleBeat(beats.current.openProg, 0)
            finish()
            return
        }

        startBeat(beats.current.openProg, 0, FZ.CLOSE_DUR, easeInOut, 0, finish)
    }, [])

    /* ── mount: measure, physics loop, input, theme, visibility ───────────── */
    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const e = engine.current
        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

        e.reduced = motionQuery.matches
        e.lastInput = performance.now()

        setGlass(supportsGlassBackdrop())

        /* ── layout (§5) */
        const measure = () => {
            const w = section.clientWidth
            const h = section.clientHeight
            if (!w || !h) return

            const mobile = w < 760
            const rect = section.getBoundingClientRect()

            e.w = w
            e.h = h
            e.mobile = mobile
            e.viewCenter = w / 2
            e.rectLeft = rect.left
            e.rectTop = rect.top
            e.cardW = mobile ? Math.min(w * 0.56, 300) : Math.min(w * 0.155, 300)
            e.cardH = e.cardW * (mobile ? 1.4 : 0.95)
            e.bandY = h * (mobile ? 0.5 : 0.55)
            e.stride = mobile ? FZ.STRIDE_FRAC_MOBILE : FZ.STRIDE_FRAC

            for (const card of cardRefs.current) {
                if (!card) continue
                card.style.width = `${e.cardW}px`
                card.style.height = `${e.cardH}px`
            }

            if (detailRef.current) {
                detailRef.current.style.width = `${e.cardW}px`
                detailRef.current.style.height = `${e.cardH}px`
            }
        }

        measure()

        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(section)

        const intersectionObserver = new IntersectionObserver(
            entries => {
                for (const entry of entries) e.inView = entry.isIntersecting
            },
            { threshold: 0.08 }
        )
        intersectionObserver.observe(section)

        /* ── the loop (§8) */
        let raf = 0
        let last = 0

        const frame = (now: number) => {
            raf = requestAnimationFrame(frame)

            const seconds = now / 1000
            const dt = last === 0 ? 1 / 60 : Math.min(seconds - last, 1 / 30)
            last = seconds

            if (!e.inView || document.hidden || !e.w) return

            e.elapsed += dt

            stepBeat(beats.current.chromeVis, dt)
            stepBeat(beats.current.openProg, dt)

            const op = beats.current.openProg.value
            const reduced = e.reduced

            /* 1 — scroll physics (§10.1) */
            if (e.open || op > 0.0001) {
                e.x = e.openScrollRef + e.openRel * op
                e.v = 0
            } else if (e.arrowTarget !== null) {
                const d = e.arrowTarget - e.x

                if (Math.abs(d) < 0.002 || reduced) {
                    e.x = e.arrowTarget
                    e.arrowTarget = null
                } else {
                    e.x += d * FZ.TARGET_EASE
                }

                e.v = 0
            } else if (!e.drag.active) {
                if (reduced) {
                    e.v = 0
                    e.x = Math.round(e.x)
                } else if (Math.abs(e.v) < FZ.SNAP_VEL && now - e.lastInput > FZ.INPUT_SNAP_DELAY) {
                    e.v = 0

                    const nearest = Math.round(e.x)
                    const d = nearest - e.x

                    e.x = Math.abs(d) < 0.0015 ? nearest : e.x + d * FZ.SNAP_STRENGTH
                } else {
                    e.x += e.v
                    e.v *= FZ.FRICTION
                }
            }

            /* 2 — decaying velocity scalar for the blur (§12) */
            const cardsPerFrame = (Math.abs(e.x - e.prevX) * (1 / 60)) / Math.max(dt, 1e-4)
            e.prevX = e.x

            const instantV = Math.min(cardsPerFrame / FZ.CARD_V_PEAK, 1)
            const openDelta = Math.abs(op - e.prevOpen)
            e.prevOpen = op

            const motionV = Math.min(1, Math.max(instantV, openDelta * 9))
            e.blurV = reduced ? 0 : Math.max(e.blurV * 0.9, motionV)

            /* intro sub-progresses (§11) */
            const introDone = reduced || e.elapsed >= FZ.ZOOM_AT + FZ.ZOOM_DUR
            const lineT = reduced ? 1 : easeInOut(clamp((e.elapsed - FZ.LINE_AT) / FZ.LINE_DUR, 0, 1))
            const zoomT = reduced ? 1 : easeInOut(clamp((e.elapsed - FZ.ZOOM_AT) / FZ.ZOOM_DUR, 0, 1))

            const { w, h, cardW, cardH, bandY, viewCenter, mobile } = e
            const sx = e.x

            let ocX = 0
            let ocY = 0
            let ocZ = 0
            let ocRot = 0
            let ocScale = 1

            /* 3 — per panel */
            for (let i = 0; i < N; i++) {
                const card = cardRefs.current[i]
                if (!card) continue

                let rel = ((i - sx) % N + N) % N
                if (rel > N / 2) rel -= N

                const offset = rel
                const ao = Math.abs(offset)

                /* settled wall geometry (§9) */
                const cfX = viewCenter + offset * (w * e.stride) - cardW / 2
                const cfZ = Math.min(ao, 3.2) * FZ.ZSTEP
                const cfRot = -offset * FZ.ROT_STEP
                const cfScale = 1 + Math.min(ao, 3.2) * FZ.EDGE_GROW
                const cfY = bandY - cardH / 2
                const edgeFade = clamp((3.7 - ao) / 1.3, 0, 1)

                let tx = cfX
                let ty = cfY
                let useRot = cfRot
                let useZ = cfZ
                let scale = cfScale
                let opacity = edgeFade
                let appearT = 1
                let appearBlur = 0

                if (!introDone) {
                    appearT = easeOutCubic(
                        clamp(
                            (e.elapsed - FZ.BLACK_HOLD - (offset + N / 2) * FZ.APPEAR_STAGGER) /
                                FZ.APPEAR_DUR,
                            0,
                            1
                        )
                    )

                    const domeT = clamp((offset + 0.6) / 4, -1, 1)

                    const cascadeX = viewCenter + offset * (w * (mobile ? 0.3 : 0.12)) - cardW / 2
                    const cascadeY =
                        cfY -
                        h * (mobile ? 0.06 : 0.15) * ((1 + Math.cos(domeT * Math.PI)) / 2) +
                        offset * h * (mobile ? 0.016 : 0.03) +
                        Math.sin(offset * 1.7 + 0.4) * h * (mobile ? 0.018 : 0.04)

                    const appearRise = (1 - appearT) * h * 0.12
                    const appearScaleF = 0.5 + 0.5 * appearT
                    appearBlur = (1 - appearT) * 0.85

                    const lineX = viewCenter + offset * (w * (mobile ? 0.28 : 0.092)) - cardW / 2
                    const lineY = cfY

                    const preX = cascadeX + (lineX - cascadeX) * lineT
                    const preY = cascadeY + appearRise + (lineY - cascadeY) * lineT
                    const preScale = FZ.LINE_SCALE * (0.72 + 0.28 * lineT) * appearScaleF

                    tx = preX + (cfX - preX) * zoomT
                    ty = preY + (cfY - preY) * zoomT
                    useRot = cfRot * zoomT
                    useZ = cfZ * zoomT
                    scale = preScale + (cfScale - preScale) * zoomT
                    opacity = appearT * (1 - (1 - edgeFade) * zoomT)
                }

                /* open / close (§14) */
                if (i === e.openIdx) {
                    ocX = tx
                    ocY = ty
                    ocZ = useZ
                    ocRot = useRot
                    ocScale = scale

                    if (op > 0.02) opacity = 0
                } else if (op > 0) {
                    useRot += -Math.sign(offset) * 30 * op
                    useZ -= 300 * op
                    opacity *= 1 - clamp(op * 1.3, 0, 1)
                }

                /* motion blur + chromatic aberration (§12) */
                const restEdge = clamp((ao - 1.4) * 0.42, 0, 0.62)
                const introBlur = introDone ? 0 : clamp(zoomT * (1 - zoomT) * 4.2, 0, 1)
                const openBlur = op > 0 && i !== e.openIdx ? clamp(op * 0.7, 0, 0.55) : 0
                const velTerm = e.blurV * (0.82 + Math.min(ao, 3) * 0.12)

                const cardV = reduced
                    ? 0
                    : Math.max(velTerm, restEdge, introBlur * 0.95, appearBlur, openBlur)

                const level = clamp(Math.round(cardV * 17), 0, 17)

                /* write, cached */
                const cache = e.cache[i]

                const transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, ${useZ.toFixed(2)}px) rotateY(${useRot.toFixed(2)}deg) scale(${scale.toFixed(4)})`
                if (cache.transform !== transform) {
                    card.style.transform = transform
                    cache.transform = transform
                }

                const nextOpacity = opacity.toFixed(3)
                if (cache.opacity !== nextOpacity) {
                    card.style.opacity = nextOpacity
                    cache.opacity = nextOpacity
                }

                const zIndex = String(Math.round(100 + ao * 2))
                if (cache.zIndex !== zIndex) {
                    card.style.zIndex = zIndex
                    cache.zIndex = zIndex
                }

                const veil = clamp((ao - 2.4) * 0.28, 0, 0.4).toFixed(3)
                if (cache.veil !== veil) {
                    const veilEl = veilRefs.current[i]
                    if (veilEl) veilEl.style.opacity = veil
                    cache.veil = veil
                }

                if (cache.level !== level) {
                    const img = imgRefs.current[i]
                    if (img) img.style.filter = level > 0 ? `url(#fz-ca-${level})` : 'none'
                    cache.level = level
                }
            }

            /* 4 — the open hero (§14) */
            const detail = detailRef.current

            if (detail) {
                if (op > 0.02 && e.openIdx >= 0) {
                    const bigScale = Math.min((w * 0.52) / cardW, (h * 0.76) / cardH)
                    const cx = viewCenter - cardW / 2
                    const cy = (h - cardH) / 2

                    const lx = ocX + (cx - ocX) * op
                    const ly = ocY + (cy - ocY) * op
                    const lz = ocZ * (1 - op)
                    const lrot =
                        ocRot * (1 - op) +
                        Math.sin(op * Math.PI) *
                            Math.max(0, 26 - Math.abs(ocRot)) *
                            Math.sign(ocRot || 1)
                    const lsc = ocScale + (bigScale - ocScale) * op

                    if (detail.style.display !== 'block') detail.style.display = 'block'

                    detail.style.transform = `translate3d(${lx.toFixed(2)}px, ${ly.toFixed(2)}px, ${lz.toFixed(2)}px) rotateY(${lrot.toFixed(2)}deg) scale(${lsc.toFixed(4)})`
                } else if (detail.style.display !== 'none') {
                    detail.style.display = 'none'
                }
            }

            /* 5 — chrome, progress, loupe (§16) */
            const chromeVis = beats.current.chromeVis.value
            const dimmed = (chromeVis * (1 - op)).toFixed(3)

            if (chromeRef.current) chromeRef.current.style.opacity = String(chromeVis)
            if (counterRef.current) counterRef.current.style.opacity = dimmed
            if (hintRef.current) hintRef.current.style.opacity = dimmed
            if (trackRef.current) trackRef.current.style.opacity = dimmed

            if (closeRef.current) {
                closeRef.current.style.opacity = op.toFixed(3)
                closeRef.current.style.pointerEvents = op > 0.5 ? 'auto' : 'none'
            }

            const centred = ((Math.round(sx) % N) + N) % N

            if (fillRef.current) {
                const frac = centred / (N - 1)
                fillRef.current.style.transform = `scaleX(${(0.04 + frac * 0.96).toFixed(4)})`
            }

            if (centred !== e.active) {
                e.active = centred
                setActiveIndex(centred)
            }

            /* loupe (§13) */
            const lens = e.lens
            const moving = e.drag.active || e.arrowTarget !== null || Math.abs(e.v) > 0.03
            const target = lens.hover && !e.open && !moving ? 1 : 0

            if (lens.vis <= 0.015 && target === 1) {
                lens.px = lens.tx
                lens.py = lens.ty
                lens.vx = 0
                lens.vy = 0
            }

            lens.vis += (target - lens.vis) * 0.2

            if (reduced) {
                lens.px = lens.tx
                lens.py = lens.ty
            } else {
                const k = 700
                const c = 46

                lens.vx += (-k * (lens.px - lens.tx) - c * lens.vx) * dt
                lens.vy += (-k * (lens.py - lens.ty) - c * lens.vy) * dt
                lens.px += lens.vx * dt
                lens.py += lens.vy * dt
            }

            const lensEl = lensRef.current

            if (lensEl) {
                if (lens.vis <= 0.015) {
                    if (lensEl.style.display !== 'none') lensEl.style.display = 'none'
                } else {
                    if (lensEl.style.display !== 'flex') lensEl.style.display = 'flex'

                    lensEl.style.transform = `translate3d(${(lens.px - FZ.LENS_R).toFixed(1)}px, ${(lens.py - FZ.LENS_R).toFixed(1)}px, 0)`
                    lensEl.style.opacity = lens.vis.toFixed(3)
                }
            }
        }

        raf = requestAnimationFrame(frame)

        /* ── chrome beat (§11): 0 → 1 over 0.5s, power2.out, at CHROME_AT */
        if (e.reduced) {
            settleBeat(beats.current.chromeVis, 1)
        } else {
            startBeat(beats.current.chromeVis, 1, 0.5, easeOutQuad, FZ.CHROME_AT)
        }

        /* ── input (§10) */
        const onWheel = (event: WheelEvent) => {
            event.preventDefault()
            if (e.open) return

            const delta =
                Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY

            e.arrowTarget = null
            e.lastInput = performance.now()

            if (e.reduced) {
                if (delta !== 0) e.x = Math.round(e.x) + Math.sign(delta)
                return
            }

            e.v = clamp(e.v + delta * FZ.WHEEL_FACTOR, -FZ.MAX_WHEEL_V, FZ.MAX_WHEEL_V)
        }

        const onPointerDown = (event: PointerEvent) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return
            if (e.open) return

            const drag = e.drag

            drag.active = true
            drag.id = event.pointerId
            drag.startX = event.clientX
            drag.startY = event.clientY
            drag.lastX = event.clientX
            drag.scrollStart = e.x
            drag.axis = ''
            drag.moved = false

            e.arrowTarget = null
            e.lastInput = performance.now()
        }

        const onPointerMove = (event: PointerEvent) => {
            if (event.pointerType !== 'touch') {
                e.lens.tx = event.clientX - e.rectLeft
                e.lens.ty = event.clientY - e.rectTop
            }

            const drag = e.drag
            if (!drag.active || event.pointerId !== drag.id) return

            const dx = event.clientX - drag.startX
            const dy = event.clientY - drag.startY

            if (!drag.axis) {
                if (Math.hypot(dx, dy) <= FZ.DRAG_AXIS_LOCK) return
                drag.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
            }

            if (drag.axis !== 'h') return
            if (Math.abs(dx) > FZ.CLICK_SLOP) drag.moved = true

            e.x = drag.scrollStart - dx * FZ.DRAG_FACTOR
            e.v = -(event.clientX - drag.lastX) * FZ.DRAG_FACTOR
            drag.lastX = event.clientX
            e.lastInput = performance.now()
        }

        const onPointerUp = (event: PointerEvent) => {
            const drag = e.drag
            if (!drag.active || event.pointerId !== drag.id) return

            drag.active = false
            drag.axis = ''
            e.lastInput = performance.now()

            window.setTimeout(() => {
                drag.moved = false
            }, 0)
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (e.open) closePanel()
                return
            }

            if (!e.inView || e.open) return

            const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown'
            if (!back && !forward) return

            const tag = (event.target as HTMLElement | null)?.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA') return

            event.preventDefault()

            e.arrowTarget = Math.round(e.x) + (forward ? 1 : -1)
            e.v = 0
            e.lastInput = performance.now()
        }

        const onScroll = () => {
            const rect = section.getBoundingClientRect()
            e.rectLeft = rect.left
            e.rectTop = rect.top
        }

        const onMotionChange = (event: MediaQueryListEvent) => {
            e.reduced = event.matches
            if (event.matches) settleBeat(beats.current.chromeVis, 1)
        }

        section.addEventListener('wheel', onWheel, { passive: false })
        section.addEventListener('pointerdown', onPointerDown)
        window.addEventListener('pointermove', onPointerMove)
        window.addEventListener('pointerup', onPointerUp)
        window.addEventListener('pointercancel', onPointerUp)
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('scroll', onScroll, { passive: true })
        motionQuery.addEventListener('change', onMotionChange)

        return () => {
            cancelAnimationFrame(raf)
            resizeObserver.disconnect()
            intersectionObserver.disconnect()
            section.removeEventListener('wheel', onWheel)
            section.removeEventListener('pointerdown', onPointerDown)
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('pointerup', onPointerUp)
            window.removeEventListener('pointercancel', onPointerUp)
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('scroll', onScroll)
            motionQuery.removeEventListener('change', onMotionChange)
        }
    }, [closePanel])

    /* ── name re-reveal whenever the centred panel changes (§16) */
    useEffect(() => {
        if (engine.current.reduced) return

        const targets = [titleRef.current, descRef.current].filter(Boolean) as HTMLElement[]
        if (!targets.length) return

        /* Park at the "from" pose with no transition, then let the next frame
           turn the transition on so it plays. These nodes are visible by
           default, so a missed frame only costs the flourish, never the text. */
        for (const target of targets) {
            target.style.transition = 'none'
            target.style.transform = 'translateY(14px)'
            target.style.filter = 'blur(6px)'
        }

        const raf = requestAnimationFrame(() => {
            targets.forEach((target, index) => {
                const at = index * 0.05

                target.style.transition = `transform 0.5s ${EASE} ${at}s, filter 0.5s ${EASE} ${at}s`
                target.style.transform = 'translateY(0)'
                target.style.filter = 'blur(0px)'
            })
        })

        return () => {
            cancelAnimationFrame(raf)
        }
    }, [activeIndex, openIndex])

    const onCardEnter = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.pointerType === 'touch') return
        engine.current.lens.hover = true
    }, [])

    const onCardLeave = useCallback(() => {
        engine.current.lens.hover = false
    }, [])

    const shown = openIndex >= 0 ? PROJECTS[openIndex] : PROJECTS[activeIndex]
    const opened = openIndex >= 0 ? PROJECTS[openIndex] : null

    return (
        <section
            ref={sectionRef}
            aria-label={`Frieze — ${shown.name}`}
            className='absolute inset-0 select-none overflow-hidden'
            style={{
                background: 'var(--fz-bg)',
                color: 'var(--fz-ink)',
                touchAction: 'pan-y'
            }}
        >
            <FriezeDefs />

            <div
                className='absolute inset-0 z-10'
                style={{
                    perspective: `${FZ.PERSPECTIVE}px`,
                    perspectiveOrigin: '50% 52%',
                    transformStyle: 'preserve-3d'
                }}
            >
                {PROJECTS.map((project, index) => (
                    <button
                        key={project.id}
                        type='button'
                        aria-label={project.name}
                        data-fz-card
                        ref={node => {
                            cardRefs.current[index] = node
                        }}
                        onClick={() => openPanel(index)}
                        onPointerEnter={onCardEnter}
                        onPointerLeave={onCardLeave}
                        className='absolute left-0 top-0 block p-0 outline-none'
                        style={
                            {
                                background: project.accent,
                                opacity: 0,
                                overflow: 'visible',
                                willChange: 'transform, opacity',
                                WebkitTapHighlightColor: 'transparent',
                                '--fz-focus': project.focus
                            } as CSSProperties
                        }
                    >
                        {/* The crop is a CSS variable, not a style prop, so the loop's
                            imperative `filter` writes on this node are never clobbered. */}
                        <img
                            ref={node => {
                                imgRefs.current[index] = node
                            }}
                            src={project.poster}
                            alt={project.name}
                            loading='eager'
                            draggable={false}
                            className='block h-full w-full object-cover [object-position:var(--fz-focus)]'
                        />

                        <span
                            ref={node => {
                                veilRefs.current[index] = node
                            }}
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0'
                            style={{ background: 'var(--fz-bg)', opacity: 0 }}
                        />

                        <span
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0'
                            style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
                        />
                    </button>
                ))}

                <button
                    type='button'
                    ref={detailRef}
                    tabIndex={-1}
                    aria-hidden='true'
                    onClick={closePanel}
                    className='absolute left-0 top-0 block cursor-pointer p-0 outline-none'
                    style={
                        {
                            display: 'none',
                            zIndex: 500,
                            willChange: 'transform',
                            '--fz-focus': opened?.focus ?? '50% 50%'
                        } as CSSProperties
                    }
                >
                    {opened && (
                        <img
                            src={opened.poster}
                            alt={opened.name}
                            draggable={false}
                            className='block h-full w-full object-cover [object-position:var(--fz-focus)]'
                            style={{
                                maskImage:
                                    'radial-gradient(ellipse 100% 100% at 50% 50%, #000 60%, transparent 100%)',
                                WebkitMaskImage:
                                    'radial-gradient(ellipse 100% 100% at 50% 50%, #000 60%, transparent 100%)'
                            }}
                        />
                    )}
                </button>
            </div>

            {/* chrome */}
            <div
                ref={chromeRef}
                className='pointer-events-none absolute left-6 top-[76px] z-30 max-w-[min(78vw,520px)] sm:left-12 sm:top-16'
                style={{ opacity: 0, paddingTop: 'env(safe-area-inset-top)' }}
            >
                <h2
                    ref={titleRef}
                    className='font-serif font-normal leading-none'
                    style={{
                        fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
                        letterSpacing: '-0.01em',
                        color: 'var(--fz-ink)'
                    }}
                >
                    {shown.name}
                </h2>

                <p
                    ref={descRef}
                    className='mt-[10px] text-[13px]'
                    style={{ letterSpacing: '0.02em', color: 'var(--fz-sub)' }}
                >
                    {shown.description}
                </p>
            </div>

            <div
                ref={counterRef}
                className='pointer-events-none absolute bottom-4 left-8 z-30 text-[11px] tabular-nums sm:left-12'
                style={{ opacity: 0, letterSpacing: '0.24em', color: 'var(--fz-faint)' }}
            >
                {String(activeIndex + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
            </div>

            <div
                ref={hintRef}
                className='pointer-events-none absolute bottom-4 right-8 z-30 text-[11px] uppercase sm:right-12'
                style={{ opacity: 0, letterSpacing: '0.24em', color: 'var(--fz-faint)' }}
            >
                Scroll or drag
            </div>

            <div
                ref={trackRef}
                aria-hidden='true'
                className='pointer-events-none absolute bottom-0 left-0 right-0 z-30 h-px'
                style={{ opacity: 0, background: 'var(--fz-rule)' }}
            >
                <div
                    ref={fillRef}
                    className='h-full w-full origin-left'
                    style={{ background: 'var(--fz-rule-on)', transform: 'scaleX(0.04)' }}
                />
            </div>

            <button
                type='button'
                ref={closeRef}
                onClick={closePanel}
                className='absolute right-8 top-7 z-40 rounded-full px-4 py-1.5 text-[11px] uppercase sm:right-12 sm:top-10'
                style={{
                    opacity: 0,
                    pointerEvents: 'none',
                    letterSpacing: '0.22em',
                    border: '1px solid var(--fz-close-border)',
                    color: 'var(--fz-close-ink)'
                }}
            >
                Close
            </button>

            <div
                ref={lensRef}
                aria-hidden='true'
                className='pointer-events-none absolute left-0 top-0 z-50 items-center justify-center rounded-full'
                style={{
                    display: 'none',
                    width: 75,
                    height: 75,
                    background: 'rgba(255,255,255,0.04)',
                    boxShadow:
                        'inset 0 1px 1.5px rgba(255,255,255,0.7), inset 0 -2px 3px rgba(255,255,255,0.15), inset 0 0 0 1px rgba(255,255,255,0.28), 0 6px 20px rgba(0,0,0,0.45)',
                    backdropFilter: glass
                        ? 'url(#fz-lens-glass) blur(0.4px) saturate(1.6) brightness(1.05)'
                        : 'blur(5px) saturate(1.5) brightness(1.05)',
                    WebkitBackdropFilter: glass
                        ? 'url(#fz-lens-glass) blur(0.4px) saturate(1.6) brightness(1.05)'
                        : 'blur(5px) saturate(1.5) brightness(1.05)'
                }}
            >
                <span
                    className='text-[9px] font-semibold uppercase'
                    style={{
                        letterSpacing: '0.2em',
                        color: 'rgba(255,255,255,0.95)',
                        textShadow: '0 1px 3px rgba(0,0,0,0.6)'
                    }}
                >
                    {openIndex >= 0 ? 'Close' : 'View'}
                </span>
            </div>
        </section>
    )
}
